import { BASS_BAND_KEYS, HIGH_BAND_KEYS, SPEECH_BAND_KEYS } from './config';
import type { AggregatedBandFeature, LowLevelFrameFeatures, SituationDimensions } from './types';
import { autocorrelation, clamp01, mean, standardDeviation } from './utils';

type SocialInput = {
  lowLevel: LowLevelFrameFeatures;
  bands: AggregatedBandFeature[];
  loudnessHistoryShort: number[];
};

function normalizedBandEntropy(bands: AggregatedBandFeature[]): number {
  const energies = bands.map((band) => Math.max(0, band.energy));
  const total = energies.reduce((acc, value) => acc + value, 0);
  if (total <= 1e-9) return 0;
  const probs = energies.map((value) => value / total);
  const entropy = probs.reduce((acc, p) => (p > 0 ? acc - p * Math.log2(p) : acc), 0);
  return clamp01(entropy / Math.log2(Math.max(2, probs.length)));
}

export function deriveSocialAcousticFeatures(input: SocialInput): SituationDimensions {
  const { lowLevel, bands, loudnessHistoryShort } = input;
  const energies = bands.map((band) => band.energy);
  const energySum = energies.reduce((acc, value) => acc + value, 0);
  const maxBandEnergy = energies.length ? Math.max(...energies) : 0;
  const bandStd = standardDeviation(energies);
  const energyEntropy = normalizedBandEntropy(bands);

  const speechBands = bands.filter((band) => SPEECH_BAND_KEYS.has(band.key));
  const speechEnergy = speechBands.reduce((acc, band) => acc + band.energy, 0) / Math.max(1, speechBands.length);
  const speechVolatility = speechBands.reduce((acc, band) => acc + band.volatility, 0) / Math.max(1, speechBands.length);

  const bassBands = bands.filter((band) => BASS_BAND_KEYS.has(band.key));
  const bassEnergy = bassBands.reduce((acc, band) => acc + band.energy, 0) / Math.max(1, bassBands.length);
  const bassPersistence = bassBands.reduce((acc, band) => acc + band.persistence, 0) / Math.max(1, bassBands.length);

  const highBands = bands.filter((band) => HIGH_BAND_KEYS.has(band.key));
  const highPeakRate = highBands.reduce((acc, band) => acc + band.peakRate, 0) / Math.max(1, highBands.length);
  const broadbandVolatility = bands.reduce((acc, band) => acc + band.volatility, 0) / Math.max(1, bands.length);

  const dominance = clamp01(energySum > 0 ? maxBandEnergy / energySum : 0);
  const diffuseness = clamp01(energyEntropy * 0.7 + (1 - clamp01(bandStd * 2.5)) * 0.3);

  const turnTaking = clamp01(
    autocorrelation(loudnessHistoryShort, 2, 12) * 0.45 +
      clamp01(speechVolatility * 1.3) * 0.35 +
      clamp01((1 - dominance) * 0.5) * 0.2
  );

  const rhythmicity = clamp01(
    autocorrelation(loudnessHistoryShort, 3, 16) * 0.55 +
      bassPersistence * 0.25 +
      clamp01((1 - standardDeviation(loudnessHistoryShort) * 3) * 0.2)
  );

  const noiseIntensity = clamp01(
    lowLevel.spectralEntropy * 0.35 +
      clamp01(lowLevel.spectralFlatness * 1.4) * 0.2 +
      broadbandVolatility * 0.2 +
      highPeakRate * 0.15 +
      clamp01((1 - speechEnergy) * 0.1)
  );

  const speechCoherence = clamp01(
    speechEnergy * 0.32 +
      clamp01((1 - speechVolatility) * 0.3) +
      clamp01((1 - lowLevel.spectralFlux) * 0.18) +
      clamp01((1 - lowLevel.spectralEntropy) * 0.2)
  );

  return {
    dominance,
    diffuseness,
    turnTaking,
    rhythmicity,
    noiseIntensity,
    speechCoherence
  };
}

export function mapDimensionsToGlobalFeatures(dimensions: SituationDimensions, lowLevel: LowLevelFrameFeatures) {
  return {
    loudness: lowLevel.loudnessNormalized,
    volatility: mean([dimensions.turnTaking, dimensions.noiseIntensity]),
    entropy: lowLevel.spectralEntropy,
    rhythmicity: dimensions.rhythmicity,
    dominance: dimensions.dominance,
    diffuseness: dimensions.diffuseness
  };
}
